import { Column } from "primereact/column";
import React, { useState, useEffect } from "react";
import { Table, Card } from "@velocity-ehs/component-library";
import { SortDirection } from "../../supportFunctions/graphql/Queries";
import { useTranslation } from "react-i18next";
import { DataTableSelectEvent } from "primereact/datatable";
import { useGetCustomerApplications } from "../../../hooks/useGetCustomerApplications";
import CustomerApplicationEditSlider from "./CustomerApplicationEditSlider";
import useCustomerApplicationMutation, {
  CustomerApplicationActions,
  CustomerApplicationData,
} from "../../../hooks/useCustomerApplicationMutation";
import classes from "./CustomerApplicationsEdit.module.scss";
import classNames from "classnames/bind";

const DEFAULT_PARAMS = {
  first: 0,
  rows: 40,
  page: 0,
  sortField: "name",
  sortOrder: SortDirection.ASC,
};

interface Props {
  customerId: string;
}

const CustomerApplicationsEdit: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const cx = classNames.bind(classes);

  const [lazyParams, setLazyParams] = useState(DEFAULT_PARAMS);
  const [showSlider, setShowSlider] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(undefined);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const { applications, pagination, refetch, isFetching } =
    useGetCustomerApplications(props.customerId, lazyParams);

  const { mutations } = useCustomerApplicationMutation();

  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination]);

  const onRowSelect = (event: DataTableSelectEvent): void => {
    setSelectedApplication(event.data);
    setShowSlider(true);
  };

  const applicationUrlBody = (rowData: any) => {
    return rowData.applicationUrl ? rowData.applicationUrl : "—";
  };

  const assignedBody = (rowData: any): React.ReactElement => {
    const statusText = rowData.isAssigned
      ? t("customer_applications_assigned_status")
      : t("customer_applications_unassigned_status");

    return (
      <div className={classes.status}>
        <div
          className={cx(classes.statusBadge, {
            isAssigned: rowData.isAssigned,
          })}
        ></div>
        <span>{statusText}</span>
      </div>
    );
  };

  const handleMutation = (
    action: CustomerApplicationActions,
    data: CustomerApplicationData
  ) => {
    return mutations.mutate(action, data, props.customerId).then(() => {
      setShowSlider(false);
      refetch();
    });
  };

  return (
    <>
      <Card title={t("customer_Applications_Tab")} full>
        <Table
          data={applications}
          params={lazyParams}
          totalRecords={totalRecords}
          selectionMode="single"
          onRowSelect={onRowSelect}
          onPage={setLazyParams}
          onSort={setLazyParams}
          isLoading={isFetching}
          fullPage={false}
          testId="customer-applications-edit-table"
        >
          <Column field="name" header={t("universal_Name")} sortable></Column>
          <Column
            field="productLine.name"
            header={t("universal_solution")}
          ></Column>
          <Column
            field="applicationUrl"
            header={t("customer_applications_application_url")}
            body={applicationUrlBody}
          ></Column>
          <Column
            field="isAssigned"
            header={t("customer_applications_status")}
            body={assignedBody}
          ></Column>
        </Table>
      </Card>
      <CustomerApplicationEditSlider
        showSlider={showSlider}
        application={selectedApplication}
        onClose={() => setShowSlider(false)}
        onMutate={handleMutation}
        isLoading={mutations.isLoading}
      />
    </>
  );
};

export default CustomerApplicationsEdit;
